import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
    GET_PENDING_ROUTES,
    GET_PENDING_ROUTES_FOR_DSP,
    PendingRouteItem,
} from '../pages/delivery/splitOrder/graphql/getPendingRoutes';
import {
    BATCH_IS_AUTO_ROUTE_GRAY,
    BatchIsAutoRouteGrayResponse,
    BatchIsAutoRouteGrayVariables,
} from '../pages/delivery/splitOrder/graphql/template/batchIsAutoRouteGray';

type Mode = 'wms' | 'dsp';

export function useUnassignedDriverCount(warehouseId: number | undefined, mode: Mode): number {
    const query = mode === 'wms' ? GET_PENDING_ROUTES : GET_PENDING_ROUTES_FOR_DSP;
    const queryKey = mode === 'wms' ? 'getPendingRoutes' : 'getPendingRoutesForDsp';

    const { data: routesRawData } = useQuery(query, {
        variables: { request: { warehouseId: warehouseId! } },
        skip: !warehouseId,
        fetchPolicy: 'network-only',
    });

    const routes: PendingRouteItem[] = useMemo(
        () => (routesRawData as any)?.[queryKey] ?? [],
        [routesRawData, queryKey],
    );

    const areaIds = useMemo(() => {
        const ids = new Set<number>();
        routes.forEach(r => { if (r.areaId != null) ids.add(r.areaId); });
        return Array.from(ids);
    }, [routes]);

    const { data: grayData } = useQuery<BatchIsAutoRouteGrayResponse, BatchIsAutoRouteGrayVariables>(
        BATCH_IS_AUTO_ROUTE_GRAY,
        {
            variables: { warehouseId: warehouseId!, areaIds },
            skip: !warehouseId || areaIds.length === 0,
        },
    );

    const grayAreaIds = useMemo(() => {
        const set = new Set<number>();
        (grayData?.batchIsAutoRouteGray ?? []).forEach(r => { if (r.gray) set.add(r.areaId); });
        return set;
    }, [grayData]);

    return useMemo(() => {
        let count = 0;
        routes.forEach(route => {
            const areaId = route.areaId;
            if (areaId == null || !grayAreaIds.has(areaId)) return;
            if (route.driverId != null && route.driverId < 0) count++;
            (route.childTaskList ?? []).forEach(child => {
                if (child.driverId != null && child.driverId < 0) count++;
            });
        });
        return count;
    }, [routes, grayAreaIds]);
}
